import { useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { CreateTabContext } from '@/components/App/NewOrder/ExistedOrder';
import { DictTypes } from '@/constants';
import { keysToCamelCase } from '@/services';
import { formatPrice } from '@/services/formatter';
import { useAppDispatch, useAppSelector } from '@/store';
import { getPromosByModel, setProductDictsToInitial } from '@/store/dict.slice';

import type { CreateAutoCreditProps } from '../../../types/form-types';

const useGetStockBounds = () => {
  const dispatch = useAppDispatch();
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { dicts, stockBoundsProductsDict } = useAppSelector(
    (state) => state.dict
  );

  const { isFieldsFrozen } = useContext(CreateTabContext);
  const [isLoading, setIsLoading] = useState(false);

  const { control, setValue } = useFormContext<CreateAutoCreditProps>();
  const [model, stockBounds] = useWatch({
    control,
    name: ['markModelYear.model.code', 'stockBounds']
  });

  useEffect(() => {
    (async () => {
      if (model) {
        setIsLoading(true);
        const modelUuid = dicts?.[DictTypes.Models]?.getBy.code[model]?.uuid;
        if (modelUuid) await dispatch(getPromosByModel(modelUuid));
        setIsLoading(false);
      } else {
        dispatch(setProductDictsToInitial());
        setValue('stockBounds', []);
      }
    })();
  }, [model]);

  useEffect(() => {
    if (stockBoundsProductsDict && stockBoundsProductsDict.length > 0) {
      const isHasStockBonds =
        orderApp?.productInfo.product.stocksBonds &&
        orderApp.status !== 'draft' &&
        isFieldsFrozen;

      const newStockBounds = stockBoundsProductsDict
        .filter((item) => item.is_active)
        .map((elem) => {
          const isChecked = isHasStockBonds
            ? orderApp.productInfo.product.stocksBonds?.some(
                (value) => value.code === elem.code
              )
            : stockBounds.find((item) => item.uuid === elem.uuid)?.checked;

          const res = {
            uuid: elem.uuid,
            code: elem.code,
            name: elem.name,
            checked: isChecked || elem.enabled_by_default,
            bankUuid: elem.bank,
            bank: elem.bank_name,
            description: elem.description,
            productConditions: keysToCamelCase(elem.Products_Info),
            productModels: keysToCamelCase(elem.Promo_Products_Models),

            loanAmount: elem.loan_amount
              ? formatPrice(Number(elem.loan_amount))
              : '',
            loanPeriod: elem.loan_period || '',
            grace: elem.grace,
            initialPaymentFrom: elem.initial_payment_from,
            initialPaymentTo: elem.initial_payment_to,
            promoStartDate: elem.promo_start_date,
            promoEndDate: elem.promo_end_date,
            percentFrom: elem.percent_from
          };
          return res;
        });
      setValue('stockBounds', newStockBounds);
    } else {
      setValue('stockBounds', []);
    }
  }, [stockBoundsProductsDict, orderApp]);

  return { isLoading };
};

export default useGetStockBounds;
